import { CARD_SURFACE, SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/**
 * The Objectives page before its data: the two cards, the steps head and a
 * typical seven rows, "Watch and talk", the Home switch's row. The real step count is unknown
 * (dev has no path to measure), so a longer path is taller than this, never
 * shorter than its first seven rows. Light on purpose — `Skeleton` and class
 * strings only — since the route's pending component ships in the entry
 * chunk.
 */
const ROWS = 7

export default function PathSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
        <div className={cn(SECTION_CARD, 'flex flex-col gap-4 p-5')}>
          <div className="flex flex-col gap-1">
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-40 rounded-xs" />
            </div>
            <div className="flex h-5 items-center">
              <Skeleton className="h-3 w-3/4 rounded-xs" />
            </div>
          </div>
          <Skeleton className="mt-auto h-4 w-full rounded-xs" />
        </div>
        <div className={cn(SECTION_CARD, 'flex flex-col gap-4 p-5')}>
          <div className="flex flex-col gap-1 pl-14">
            <div className="flex h-4 items-center">
              <Skeleton className="h-3 w-16 rounded-xs" />
            </div>
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-48 rounded-xs" />
            </div>
            <div className="flex h-5 items-center">
              <Skeleton className="h-3 w-2/3 rounded-xs" />
            </div>
          </div>
          <div className="mt-auto flex items-center justify-between gap-4">
            <Skeleton className="h-3 w-16 rounded-xs" />
            <Skeleton className="h-9 w-20 rounded-md" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex min-h-9 items-center">
          <Skeleton className="h-5 w-28 rounded-xs" />
        </div>
        <div className={cn(CARD_SURFACE, 'overflow-hidden')}>
          {Array.from({ length: ROWS }, (_, index) => (
            <div
              key={index}
              className="flex min-h-16 items-center gap-4 border-b border-border-subtle px-5 py-3 last:border-b-0"
            >
              <Skeleton className="size-6 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Skeleton className="h-4 w-40 rounded-xs" />
                <Skeleton className="h-3 w-2/3 rounded-xs" />
              </div>
              <Skeleton className="h-4 w-10 rounded-xs" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex min-h-9 items-center">
          <Skeleton className="h-5 w-32 rounded-xs" />
        </div>
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>

      <div className={cn(SECTION_CARD, 'flex items-center justify-between gap-4')}>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-36 rounded-xs" />
          <Skeleton className="h-3 w-56 rounded-xs" />
        </div>
        <Skeleton className="h-7 w-11 shrink-0 rounded-full" />
      </div>
    </div>
  )
}
